import { useState, useMemo, useRef, useEffect } from 'react';

type Props = {
  letra: string | null | undefined;
  nomeHomenageado: string;
};

// Destaca todas as ocorrências do primeiro nome do homenageado na letra.
function highlightName(text: string, name: string): { node: React.ReactNode; matches: number } {
  if (!name || !text) return { node: text, matches: 0 };
  const first = name.split(/\s+/)[0] || '';
  if (first.length < 2) return { node: text, matches: 0 };

  // Aceita variações: Heloísa | heloísa | Heloisa (sem acento)
  const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const variations = new Set<string>();
  variations.add(first);
  // Versão sem acento
  variations.add(first.normalize('NFD').replace(/[̀-ͯ]/g, ''));
  const pattern = new RegExp(`\\b(${Array.from(variations).map(escape).join('|')})\\b`, 'gi');

  let matches = 0;
  const parts: React.ReactNode[] = [];
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index));
    parts.push(
      <span key={`hl-${m.index}`} className="mz-lyr-name">
        {m[0]}
      </span>,
    );
    matches += 1;
    lastIdx = m.index + m[0].length;
  }
  parts.push(text.slice(lastIdx));
  return { node: parts, matches };
}

export default function LyricsExpandable({ letra, nomeHomenageado }: Props) {
  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const trimmed = useMemo(() => (letra || '').trim(), [letra]);
  const lines = useMemo(() => trimmed.split(/\n+/).filter(Boolean), [trimmed]);

  // Mostra: estrofe inicial até primeira aparição do nome ou 4 linhas, o que vier primeiro
  const previewLines = useMemo(() => {
    const first = nomeHomenageado.split(/\s+/)[0]?.toLowerCase() || '';
    let idx = -1;
    if (first) {
      idx = lines.findIndex((l) => l.toLowerCase().includes(first));
    }
    const cut = idx >= 0 ? Math.max(idx + 1, 3) : 4;
    return lines.slice(0, Math.min(cut, 5));
  }, [lines, nomeHomenageado]);

  const previewText = previewLines.join('\n');
  const previewHighlighted = useMemo(() => highlightName(previewText, nomeHomenageado).node, [previewText, nomeHomenageado]);
  const fullHighlighted = useMemo(() => highlightName(trimmed, nomeHomenageado), [trimmed, nomeHomenageado]);

  // Animação suave de altura ao expandir
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    if (expanded) {
      el.style.maxHeight = el.scrollHeight + 'px';
    } else {
      el.style.maxHeight = '';
    }
  }, [expanded]);

  if (!trimmed) return null;

  return (
    <section className="mz-lyr-wrap" aria-label="Letra da música">
      <div className="mz-lyr-paper">
        <div className="mz-lyr-corner-tl" aria-hidden />
        <div className="mz-lyr-corner-tr" aria-hidden />
        <div className="mz-lyr-corner-bl" aria-hidden />
        <div className="mz-lyr-corner-br" aria-hidden />

        <div className="mz-lyr-head">
          <span className="mz-lyr-head-dash" />
          <span className="mz-lyr-head-text">Letra · com {fullHighlighted.matches}× <em>{nomeHomenageado.split(/\s+/)[0] || 'o nome'}</em></span>
          <span className="mz-lyr-head-dash" />
        </div>

        <div
          ref={contentRef}
          className={`mz-lyr-body ${expanded ? 'mz-lyr-expanded' : ''}`}
        >
          <pre className="mz-lyr-pre">
            {expanded ? fullHighlighted.node : previewHighlighted}
          </pre>
        </div>

        {!expanded && (
          <div className="mz-lyr-fade" aria-hidden />
        )}

        <button
          type="button"
          className="mz-lyr-toggle"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
        >
          {expanded ? (
            <>
              Esconder letra
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 15l-6-6-6 6" />
              </svg>
            </>
          ) : (
            <>
              Ver letra completa
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </>
          )}
        </button>
      </div>

      <style>{`
        .mz-lyr-wrap {
          margin: 0 16px 22px;
        }

        .mz-lyr-paper {
          position: relative;
          padding: 22px 24px 16px;
          background:
            linear-gradient(180deg, #fffdf8 0%, #fff8ef 100%),
            repeating-linear-gradient(
              transparent 0px,
              transparent 28px,
              rgba(255,145,77,0.04) 28px,
              rgba(255,145,77,0.04) 29px
            );
          background-blend-mode: normal, multiply;
          border-radius: 14px;
          border: 1px solid rgba(204,102,32,0.18);
          box-shadow:
            0 6px 24px -10px rgba(80,40,10,0.18),
            0 1px 0 rgba(255,255,255,0.95) inset;
        }

        html[data-theme='dark'] .mz-lyr-paper {
          background: rgba(255,255,255,0.04);
          border-color: rgba(255,145,77,0.18);
        }

        /* Cantos decorativos tipo caderno antigo */
        .mz-lyr-corner-tl, .mz-lyr-corner-tr, .mz-lyr-corner-bl, .mz-lyr-corner-br {
          position: absolute;
          width: 12px;
          height: 12px;
          border-color: var(--gold);
          opacity: 0.35;
        }
        .mz-lyr-corner-tl { top: 8px; left: 8px; border-top: 1.5px solid; border-left: 1.5px solid; }
        .mz-lyr-corner-tr { top: 8px; right: 8px; border-top: 1.5px solid; border-right: 1.5px solid; }
        .mz-lyr-corner-bl { bottom: 8px; left: 8px; border-bottom: 1.5px solid; border-left: 1.5px solid; }
        .mz-lyr-corner-br { bottom: 8px; right: 8px; border-bottom: 1.5px solid; border-right: 1.5px solid; }

        .mz-lyr-head {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-bottom: 14px;
        }
        .mz-lyr-head-dash {
          flex: 1;
          max-width: 36px;
          height: 1px;
          background: var(--gold-border);
          opacity: 0.65;
        }
        .mz-lyr-head-text {
          font-family: 'DM Sans', sans-serif;
          font-size: 10.5px;
          font-weight: 700;
          color: var(--amber-deep);
          letter-spacing: 0.22em;
          text-transform: uppercase;
        }
        .mz-lyr-head-text em {
          font-style: italic;
          font-family: 'Playfair Display', 'Source Serif 4', serif;
          font-size: 13px;
          font-weight: 700;
          text-transform: none;
          letter-spacing: -0.01em;
          color: var(--gold);
        }

        .mz-lyr-body {
          overflow: hidden;
          transition: max-height 0.5s cubic-bezier(0.22, 1, 0.36, 1);
          max-height: 180px;
        }
        .mz-lyr-expanded {
          max-height: 2000px;
        }

        .mz-lyr-pre {
          font-family: 'Source Serif 4', 'Playfair Display', serif;
          font-size: 17px;
          line-height: 1.85;
          color: var(--text-body);
          margin: 0;
          padding: 4px 2px 16px;
          white-space: pre-wrap;
          word-break: break-word;
          font-weight: 400;
        }

        .mz-lyr-name {
          font-family: 'Playfair Display', 'Source Serif 4', serif;
          font-weight: 700;
          font-style: italic;
          color: var(--gold);
          background: linear-gradient(180deg, transparent 60%, rgba(255,145,77,0.16) 60%);
          padding: 0 2px;
          border-radius: 2px;
        }

        .mz-lyr-fade {
          position: absolute;
          bottom: 48px;
          left: 24px;
          right: 24px;
          height: 56px;
          pointer-events: none;
          background: linear-gradient(180deg, transparent 0%, #fff8ef 90%);
        }
        html[data-theme='dark'] .mz-lyr-fade {
          background: linear-gradient(180deg, transparent 0%, rgba(20,10,28,0.65) 90%);
        }

        .mz-lyr-toggle {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin: 4px auto 0;
          width: auto;
          padding: 10px 18px;
          background: var(--gold-pale);
          color: var(--amber-deep);
          border: 1.5px solid var(--gold-border);
          border-radius: 999px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 700;
          font-size: 13px;
          cursor: pointer;
          letter-spacing: 0.02em;
          transition: background 0.18s, transform 0.18s;
          position: relative;
          z-index: 1;
        }
        .mz-lyr-toggle:hover { background: #ffe5d2; transform: translateY(-1px); }
        .mz-lyr-toggle:focus-visible {
          outline: 3px solid var(--gold);
          outline-offset: 2px;
        }

        @media (min-width: 720px) {
          .mz-lyr-wrap { margin: 0 auto 26px; max-width: 660px; }
          .mz-lyr-pre { font-size: 18.5px; }
        }
      `}</style>
    </section>
  );
}
